<template>
  <!-- TODO: consider styling options for this component -->
  <!--   like the spinner, buttons, menus etc. -->
  <div>
    <b-button v-b-modal.translation-import-export-modal>
      {{ $t('translations.translation_import_export') }}
    </b-button>

    <b-modal id="translation-import-export-modal" :title="$t('translations.translation_import_export')" :hide-footer="true">
      <div v-if="notLoaded" class="text-center">
        <b-spinner />
      </div>
      <div v-else>
        <b-alert :show="alert.seconds" :variant="alert.type" @dismiss-count-down="setAlertSeconds">
          <p>{{ alert.message }}</p>
          <b-progress :value="alert.seconds" :max="alert.maxSeconds" :variant="alert.type" height="1px" />
        </b-alert>

        <b-row class="ml-1 mb-3">
          <b-dropdown :text="currentLanguage?.name || $t('translations.language')"
            :variant="currentLanguage?.name ? 'primary' : ''">
            <b-dropdown-item v-for="language in languages" :key="language.id" @click="setCurrentLanguage(language)">
              {{ language.name }}
            </b-dropdown-item>
          </b-dropdown>
        </b-row>

        <b-row class="mx-1 mb-3">
          <b-form-file v-model="file" variant="primary" :placeholder="$t('translations.choose_or_drop_in_file')"
            :drop-placeholder="$t('translations.drop_in_file')" :browse-text="$t('translations.browse')">
            <template slot="file-name" slot-scope="{ files }">
              <b-badge variant="primary">
                {{ files[0].name }}
              </b-badge>
            </template>
          </b-form-file>
        </b-row>
        <b-row class="ml-1 mb-3">
          <b-form-checkbox v-model="overwriteExisting" switch variant="primary">
            {{ $t('translations.overwrite_existing') }}
          </b-form-checkbox>
        </b-row>

        <b-row class="ml-1 mb-3">
          <b-col md="auto" class="pl-0 pr-3">
            <b-button @click="runImport">
              {{ $t('translations.import') }}
            </b-button>
          </b-col>
          <b-col md="auto" class="pl-0 pr-3">
            <b-button @click="runExport">
              {{ $t('translations.export') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import languagesService from '@/services/languages';
import {i18n} from '@/_translate';

export default {
  name: 'TranslationImportExport',
  data() {
    return {
      alert: {
        maxSeconds: 3,
        seconds: 0,
        message: '',
        type: '',
      },
      languages: [],
      currentLanguage: null,
      file: null,
      overwriteExisting: false,
    };
  },
  computed: {
    loaded() {
      return this.languages?.length > 0;
    },
    notLoaded() {
      return !this.loaded;
    },
    fileIsValid() {
      return Boolean(this.file) || null;
    },
  },
  mounted() {
    languagesService.classifier().then((response) => {
      /** DOC: Expected response schema:
      {
        "data": {
          "data": [
            {
              "id": 3,
              "name": "lv",
              "key": "lv",
            },
            {
              "id": 2,
              "name": "Reference",
              "key": "xx",
            },
            {
              "id": 1,
              "name": "English",
              "key": "en",
            }
          ]
        },
        "status": 200,
        "headers": { ... },
        ...,
      }
      */
      // TODO consider refactoring of assumed service method name and expected response schema
      this.languages = response.data.data;
    }).catch((data) => {
      console.error('Got error when listing languages.', data);
    });
  },
  methods: {
    setCurrentLanguage(language) {
      this.currentLanguage = language;
    },
    runImport() {
      try {
        this.validateLanguageIsSet();
        this.validateFileIsSet();
      } catch {
        return;
      }

      this.file.text().then((fileText) => {
        /**
         * DOC:
         * Assumes that the request body must have the following fields.
         * `data` with a JSON string
         * `overwriteExisting` with a boolean
         * Example:
         *   {
         *     data: "{ \"is\": \"a JSON\", \"not\": \"an object\" }",
         *     overwriteExisting: false
         *   }
         */
        const body = {
          data: fileText,
          overwriteExisting: this.overwriteExisting,
        };
        languagesService.importTranslations(this.currentLanguage.id, body).then((response) => {
          this.displaySuccess(this.$t('translations.import_successful'));
        }).catch((data) => {
          const response = data?.response;
          if (response?.status >= 400 && response?.status <= 499) {
            this.displayError(response?.data?.error_message || this.$t('validation.unknown_client_error'));
          }
          console.error('Got error when importing translations.', data);
        });
      });
    },
    runExport() {
      try {
        this.validateLanguageIsSet();
      } catch {
        return;
      }

      languagesService.exportTranslations(this.currentLanguage.id).then((response) => {
        // DOC: Assumes that `response` will have a `data` object to read
        downloadJSON(
          `${this.currentLanguage.key}_translations.json`,
          JSON.stringify(response.data),
        );
        this.displaySuccess(this.$t('translations.export_successful'));
      }).catch((data) => {
        console.error('Got error when exporting translations.', data);
      });
    },
    validateLanguageIsSet() {
      if (!this.currentLanguage) {
        this.displayAndThrowError(this.$t('validation.language_required'));
      }
    },
    validateFileIsSet() {
      if (!this.file) {
        this.displayAndThrowError(this.$t('validation.file_required'));
      }
    },
    displaySuccess(msg) {
      this.alert.message = msg;
      this.alert.type = 'success';
      this.setAlertSeconds(this.alert.maxSeconds);
    },
    displayError(msg) {
      this.alert.message = msg;
      this.alert.type = 'danger';
      this.setAlertSeconds(this.alert.maxSeconds);
    },
    displayAndThrowError(msg) {
      this.displayError(msg);
      throw msg;
    },
    setAlertSeconds(seconds) {
      this.alert.seconds = seconds;
    },
  },
};

function downloadJSON(name, content) {
  const data = `data:text/json;charset=utf-8,${encodeURIComponent(content)}`;

  const a = document.createElement('a');
  a.setAttribute('href', data);
  a.setAttribute('download', name);
  document.body.appendChild(a);

  a.click();
  a.remove();
}
</script>
